import React from "react";
import { log } from '@/lib/utils/logger';
import sanitizeHtml from '@/lib/utils/sanitizeHtml';
import { SectionComponent } from "@/store/sagas/types/deviceUnlock";
import InputRenderer from "./InputRenderer";
import CTARenderer from "./CTARenderer";
import ModalComponent from "./CommonComponents/ModalComponent";

interface SectionRendererProps {
    section: SectionComponent;
    onSubmit?: (data: Record<string, unknown>) => void;
    onNavigate?: (destination: string) => void;
    isSubmitting?: boolean;
    hasInputError?: boolean;
    onInputValidationChange?: (inputId: string, hasError: boolean) => void;
}

const SectionRenderer: React.FC<SectionRendererProps> = ({
    section,
    onSubmit,
    onNavigate,
    isSubmitting,
    hasInputError = false,
    onInputValidationChange
}) => {
    const [showPopup, setShowPopup] = React.useState(false);
    const htmlRef = React.useRef<HTMLDivElement>(null);

    // Use event delegation for reliable popup on <a> click
    React.useEffect(() => {
        if (!section.value || !htmlRef.current) return;

        const links = htmlRef.current.querySelectorAll('a');
        links.forEach((link) => {
            link.setAttribute('role', 'button');
            link.setAttribute('aria-haspopup', 'dialog');
        });

        const handler = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            // Check if the clicked element is a link or inside a link
            const link = target.tagName === 'A' ? target : target.closest('a');
            if (link) {
                e.preventDefault();
                e.stopPropagation();
                const content = link.getAttribute('data-popup-content') || link.textContent || 'Unlock Policy';
                log.debug('Link clicked, opening popup with content:', content);
                setShowPopup(true);
            }
        };

        const keydownHandler = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;
            const link = target.tagName === 'A' ? target : target.closest('a');
            if (link && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                e.stopPropagation();
                setShowPopup(true);
            }
        };

        const container = htmlRef.current;
        container.addEventListener('click', handler, true); // Use capture phase
        container.addEventListener('keydown', keydownHandler, true);
        return () => {
            container.removeEventListener('click', handler, true);
            container.removeEventListener('keydown', keydownHandler, true);
        };
    }, [section.value]);

    return (
        <div className="mb-6" key={section.id}>
            {section.title && (
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{section.title}</h3>
            )}
            {/* Render HTML value with popup handler for <a> */}
            {section.value && (
                <>
                    <div
                        ref={htmlRef}
                        className="text-gray-700 mb-4"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.value || '') }}
                    />
                    <button
                        type="button"
                        className="mb-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        onClick={() => { setShowPopup(true); }}
                    >
                        Test Popup
                    </button>
                </>
            )}
            {section.input && <InputRenderer input={section.input} onValidationChange={onInputValidationChange} />}
            {section.cta && (
                <CTARenderer
                    cta={section.cta}
                    onNavigate={onNavigate}
                    isSubmitting={isSubmitting}
                    hasInputError={hasInputError}
                />
            )}
            {section.content?.section && (
                <div className="space-y-4">
                    {section.content.section.map((subsection) => (
                        <SectionRenderer
                            key={subsection.id}
                            section={subsection}
                            onSubmit={onSubmit}
                            onNavigate={onNavigate}
                            isSubmitting={isSubmitting}
                            hasInputError={hasInputError}
                            onInputValidationChange={onInputValidationChange}
                        />
                    ))}
                </div>
            )}
            {section.footer && (
                <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-400">
                    <h4 className="font-semibold text-yellow-800 mb-2">{section.footer.title}</h4>
                    <p className="text-yellow-700 text-sm whitespace-pre-line">{section.footer.body}</p>
                </div>
            )}
            <ModalComponent
                open={showPopup}
                onClose={() => setShowPopup(false)}
                title="Unlock Policy"
                maxWidth={'lg'}
            >
                {/* <p>{popupContent}</p> */}
                This is Modal Component Content for Unlock Policy. ........
            </ModalComponent>
        </div>
    );
};

export default SectionRenderer;